import { isImmutable } from 'immutable';
import merge from 'lodash/merge';
import { DateTime } from 'luxon';
import type { SagaIterator } from 'redux-saga';
import { call, put, race, select, take, takeEvery, takeLeading } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';

import type { generatedReportsTypes } from 'app-components/flux/generated-reports';
import { generatedReportsActions } from 'app-components/flux/generated-reports';
import { reportConfigurationsTypes } from 'app-components/flux/report-configurations';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
  scheduledTransactionsTypes,
  scheduledTransactionsUtils,
} from 'app-components/flux/scheduled-transactions';
import { transactionsTypes } from 'app-components/flux/transactions';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import type { ResourceActionCreator, ResourceFailureActionCreator } from '../core/resourceStoreTypes';
import { DeductionType } from '../mileage-tracker/mileageTrackerDeductionForm';
import * as actions from './taxesActions';
import * as taxesSelectors from './taxesSelectors';
import {
  transformServerResourceToClientResource,
  transformTaxInputClientResourceToRequestData,
  transformTaxInputServerResourceToClientResource,
} from './taxesTransformers';
import * as taxesTypes from './taxesTypes';
import * as taxesUtils from './taxesUtils';

const mkQcsSyncTaxInput = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<taxesTypes.TaxInputProps>();
const mkTaxInputResourceConfig = (
  successAction: ResourceActionCreator<taxesTypes.TaxInputProps>,
  failureAction: ResourceFailureActionCreator,
) =>
  mkQcsSyncTaxInput({
    resourceName: 'taxes',
    resourceBaseUrl: '/taxes/tax-inputs',
    getLastSyncDate: taxesSelectors.getLastSyncDate,

    transformClientResourceToRequestData: transformTaxInputClientResourceToRequestData,
    transformServerResourceToClientResource: transformTaxInputServerResourceToClientResource,

    successAction,
    failureAction,
  });

const mkQcsSyncTaxConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<taxesTypes.TaxProps>();
const mkTaxResourceConfig = (
  successAction: ResourceActionCreator<taxesTypes.TaxProps>,
  failureAction: ResourceFailureActionCreator,
) =>
  mkQcsSyncTaxConfig({
    resourceName: 'taxes',
    resourceBaseUrl: '/taxes/tax-calculations',
    getLastSyncDate: taxesSelectors.getLastSyncDate,

    transformServerResourceToClientResource,

    successAction,
    failureAction,
  });

export function* getTaxCalculationsActionWatcher() {
  const resourceConfig = mkTaxResourceConfig(actions.getTaxCalculationsSuccess, actions.getTaxCalculationFailure);
  yield resourceSagaUtils.takeAndDebounce(
    actions.getTaxCalculations.type,
    resourceSagaUtils.qcsSyncGetResources<taxesTypes.TaxProps>,
    resourceConfig,
  );
}

export function* getTaxInputsActionWatcher() {
  const resourceConfig = mkTaxInputResourceConfig(actions.getTaxInputsSuccess, actions.getTaxInputsFailure);
  yield takeEvery(
    actions.getTaxInputs.type,
    resourceSagaUtils.qcsSyncGetResources<taxesTypes.TaxInputProps>,
    resourceConfig,
  );
}

export function* createTaxInputActionWatcher() {
  const resourceConfig = mkTaxInputResourceConfig(actions.createTaxInputSuccess, actions.createTaxInputFailure);
  yield takeEvery(
    actions.createTaxInput.type,
    resourceSagaUtils.qcsSyncCreateResource<taxesTypes.TaxInputProps>,
    resourceConfig,
  );
}

export function* updateTaxInputActionWatcher() {
  const resourceConfig = mkTaxInputResourceConfig(actions.updateTaxInputSuccess, actions.updateTaxInputFailure);
  yield takeEvery(
    actions.updateTaxInput.type,
    resourceSagaUtils.qcsSyncUpdateResource<taxesTypes.TaxInputProps>,
    resourceConfig,
  );
}

export function* deleteTaxInputActionWatcher() {
  const resourceConfig = mkTaxInputResourceConfig(actions.deleteTaxInputSuccess, actions.deleteTaxInputFailure);
  yield takeEvery(
    actions.deleteTaxInput.type,
    resourceSagaUtils.qcsSyncDeleteResource<taxesTypes.TaxInputProps>,
    resourceConfig,
  );
}

export function* createTaxInputFailureActionWatcher() {
  yield takeEvery([actions.createTaxInputFailure.type, actions.updateTaxInputFailure.type], actions.getTaxCalculations);
}

export function* performTaxCalcActionWatcher() {
  yield takeEvery(actions.performTaxCalc.type, resourceSagaUtils.qcsRequest, {
    method: 'POST',
    path: (action: ReturnType<typeof actions.performTaxCalc>) => `/taxes/tax-calculations/calc/${action.payload}`,
    successAction: actions.performTaxCalcSuccess,
    failureAction: actions.performTaxCalcFailure,
  });
}

const getValidNumber = (value?: number) => (typeof value === 'number' && isFinite(value) ? value : undefined);

const getAnnualValue = (
  taxYear: number,
  yearToDateValue?: number,
  inputSource?: taxesTypes.Source,
  inputValue?: number,
) => {
  switch (inputSource) {
    case 'YEAR_TO_DATE_REPORT':
      return getValidNumber(yearToDateValue);
    case 'ANNUAL_ESTIMATE':
      return taxesUtils.estimatedAnnualAmount(taxYear, yearToDateValue);
    case 'ANNUAL_USER_ENTERED':
      return getValidNumber(inputValue);
  }
  return undefined;
};

const bundleOrUndefined = (bundle: taxesTypes.ValueBundleProps) =>
  getValidNumber(bundle?.value) === undefined ? undefined : bundle;

const TAX_PLAN_REPORT_LOCATION = 'tax-planner-tax-summary';

const isTaxPlanReportSuccess = (action: unknown) =>
  generatedReportsActions.generateReportSuccess.match(action) && action.payload.location === TAX_PLAN_REPORT_LOCATION;

const isTaxPlanReportFailure = (action: unknown) =>
  generatedReportsActions.generateReportFailure.match(action) && action.payload.location === TAX_PLAN_REPORT_LOCATION;

type TaxCalcSuccessAction = {
  type: string;
  payload?: {
    data?: {
      resource?: {
        output?: { withholdingsAndSummaryCalc?: { remainingTaxDue: number } };
      };
    };
  };
  meta?: { context?: string };
};

type TaxCalcFailureAction = {
  type: string;
  payload?: unknown;
  meta?: { context?: string };
};

type TaxCalcRaceResult = {
  performTaxCalcSuccess?: TaxCalcSuccessAction;
  performTaxCalcFailure?: TaxCalcFailureAction;
};

const getTaxCalcRequestContext = (taxPlanId: string): string => `TaxPlanRefresh:${taxPlanId}`;

export function* performTaxCalcForPlan(taxPlanId: string): SagaIterator<TaxCalcRaceResult> {
  const requestContext = getTaxCalcRequestContext(taxPlanId);
  yield put(actions.performTaxCalc(taxPlanId, { context: requestContext }));

  return yield race({
    performTaxCalcSuccess: take(
      (candidate: TaxCalcSuccessAction) =>
        candidate.type === actions.performTaxCalcSuccess.type && candidate.meta?.context === requestContext,
    ),
    performTaxCalcFailure: take(
      (candidate: TaxCalcFailureAction) =>
        candidate.type === actions.performTaxCalcFailure.type && candidate.meta?.context === requestContext,
    ),
  });
}

export function* performTaxPlanRefreshActionWatcher() {
  yield takeLeading(
    actions.performTaxPlanRefresh.type,
    function* performTaxPlanRefresh(
      action: ReturnType<typeof actions.performTaxPlanRefresh>,
    ): Generator<any, any, any> {
      try {
        if (!action.payload.id && !action.payload.year) {
          yield put(actions.performTaxPlanRefreshFailure(new Error('Missing parameters')));
          return { state: 'MISSING_PARAMS_ERROR' };
        }

        // fetch latest to avoid loops accross multiple tabs
        yield put(actions.getTaxInputs());
        yield race({
          getTaxInputsSuccess: take(actions.getTaxInputsSuccess.type),
          getTaxInputsFailure: take(actions.getTaxInputsFailure.type),
        });

        const taxPlan: taxesTypes.Tax | undefined = yield select(
          taxesSelectors.getTaxPlanById,
          action.payload.id || '',
        );
        const year = action.payload.year || taxPlan?.input?.year;
        if (year) {
          // generate tax report for the year
          const startOn = DateTime.fromObject({ year, month: 1, day: 1 }).toISODate() || undefined;
          const endOn = DateTime.fromObject({ year, month: 12, day: 31 }).toISODate() || undefined;
          const reportConfig = reportConfigurationsTypes.mkReportConfiguration({
            name: 'taxes-plan',
            accountingType: 'CASH',
            deductionType: DeductionType.ACTUAL,
            reportType: 'TRANSACTION',
            reportSubType: 'TAX_SUMMARY',
            persistentType: 'TEMPORARY',
            dateRange: 'CUSTOM',
            startOn,
            endOn,
            columnType: 'NONE',
            rowType: 'USAGE',
          });
          yield put(
            generatedReportsActions.generateReport({
              location: TAX_PLAN_REPORT_LOCATION,
              configuration: reportConfig,
            }),
          );
          // The dashboard P&L and net worth cards generate their own reports, so an
          // unfiltered take would map whichever responded first as the tax summary.
          const { generateReportSuccess } = yield race({
            generateReportSuccess: take(isTaxPlanReportSuccess),
            generateReportFailure: take(isTaxPlanReportFailure),
          });
          if (!generateReportSuccess) {
            assert(false, 'tax planner: generate report error', generateReportSuccess?.payload);
            action.meta?.reject?.({ state: 'TAX_REPORT_ERROR' });
            yield put(actions.performTaxPlanRefreshFailure(new Error('Tax report generation failed')));
            return { state: 'TAX_REPORT_ERROR' };
          }

          // apply transactions and overrides on top of the tax report
          let taxInput;
          try {
            const taxQuarters = yield select(taxesSelectors.getTaxQuarters, action.payload.id);
            const taxInputReport = taxesUtils.mapTaxReportToTaxInput(
              generateReportSuccess.payload.report as generatedReportsTypes.Report,
            );
            const taxInputResolved = merge(
              isImmutable(taxPlan?.input) ? taxPlan?.input.toJS() : taxPlan?.input,
              isImmutable(action.payload) ? action.payload.toJS() : action.payload,
            ) as taxesTypes.TaxInputProps;
            const autoAnnualize = true;
            // TODO: figure out better mechanism to override report values with user input
            // TODO: if QCS can't hookup qXBizIncome and qXBizDeductions to calc directly - clear overdue overrides
            const autoAnnualizeBusiness =
              !taxInputResolved?.profile?.q4BizDeductions?.sourceAttributes ||
              taxInputResolved?.profile?.q4BizDeductions?.sourceAttributes === 'ANNUAL_ESTIMATE';
            let bizIncomeAdjustment = 0;
            let bizDeductionsAdjustment = 0;
            if (action.payload.id) {
              const quarterAdjustments = yield select(taxesSelectors.getQuarterAdjustments, action.payload.id, {
                q1BizIncome: taxInputResolved?.profile?.q1BizIncome?.value || 0,
                q2BizIncome: taxInputResolved?.profile?.q2BizIncome?.value || 0,
                q3BizIncome: taxInputResolved?.profile?.q3BizIncome?.value || 0,
                q4BizIncome: taxInputResolved?.profile?.q4BizIncome?.value || 0,
                q1BizDeductions: taxInputResolved?.profile?.q1BizDeductions?.value || 0,
                q2BizDeductions: taxInputResolved?.profile?.q2BizDeductions?.value || 0,
                q3BizDeductions: taxInputResolved?.profile?.q3BizDeductions?.value || 0,
                q4BizDeductions: taxInputResolved?.profile?.q4BizDeductions?.value || 0,
              });
              bizIncomeAdjustment =
                (quarterAdjustments?.q1.bizIncome ?? 0) +
                (quarterAdjustments?.q2.bizIncome ?? 0) +
                (quarterAdjustments?.q3.bizIncome ?? 0) +
                (quarterAdjustments?.q4.bizIncome ?? 0);
              bizDeductionsAdjustment =
                (quarterAdjustments?.q1.bizDeductions ?? 0) +
                (quarterAdjustments?.q2.bizDeductions ?? 0) +
                (quarterAdjustments?.q3.bizDeductions ?? 0) +
                (quarterAdjustments?.q4.bizDeductions ?? 0);
            }

            merge<taxesTypes.TaxInputProps, taxesTypes.TaxInputProps>(taxInputReport, {
              ...(!action.payload.id && { clientId: uuidv4().toUpperCase() }),
              taxPayments: {
                estimatedTaxesPaidOrPlannedApril: { value: taxQuarters.get(0)?.amountPaid },
                estimatedTaxesPaidOrPlannedJune: { value: taxQuarters.get(1)?.amountPaid },
                estimatedTaxesPaidOrPlannedSeptember: { value: taxQuarters.get(2)?.amountPaid },
                estimatedTaxesPaidOrPlannedJanuary: { value: taxQuarters.get(3)?.amountPaid },
              },
              year,
              filingStatus: taxPlan?.input?.filingStatus,
              ...action.payload,
              profile: {
                numberOfDependents: taxPlan?.input?.profile?.numberOfDependents,
                stModelId: taxPlan?.input?.profile?.stModelId,
                q1BizIncome: bundleOrUndefined({
                  value: autoAnnualizeBusiness ? 0 : taxInputResolved?.profile?.q1BizIncome?.value,
                  sourceAttributes: autoAnnualizeBusiness
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.profile?.q1BizIncome?.sourceAttributes,
                }),
                q2BizIncome: bundleOrUndefined({
                  value: autoAnnualizeBusiness ? 0 : taxInputResolved?.profile?.q2BizIncome?.value,
                  sourceAttributes: autoAnnualizeBusiness
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.profile?.q2BizIncome?.sourceAttributes,
                }),
                q3BizIncome: bundleOrUndefined({
                  value: autoAnnualizeBusiness ? 0 : taxInputResolved?.profile?.q3BizIncome?.value,
                  sourceAttributes: autoAnnualizeBusiness
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.profile?.q3BizIncome?.sourceAttributes,
                }),
                q4BizIncome: bundleOrUndefined({
                  value: autoAnnualizeBusiness ? 0 : taxInputResolved?.profile?.q4BizIncome?.value,
                  sourceAttributes: autoAnnualizeBusiness
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.profile?.q4BizIncome?.sourceAttributes,
                }),
                q1BizDeductions: bundleOrUndefined({
                  value: autoAnnualizeBusiness ? 0 : taxInputResolved?.profile?.q1BizDeductions?.value,
                  sourceAttributes: autoAnnualizeBusiness
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.profile?.q1BizDeductions?.sourceAttributes,
                }),
                q2BizDeductions: bundleOrUndefined({
                  value: autoAnnualizeBusiness ? 0 : taxInputResolved?.profile?.q2BizDeductions?.value,
                  sourceAttributes: autoAnnualizeBusiness
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.profile?.q2BizDeductions?.sourceAttributes,
                }),
                q3BizDeductions: bundleOrUndefined({
                  value: autoAnnualizeBusiness ? 0 : taxInputResolved?.profile?.q3BizDeductions?.value,
                  sourceAttributes: autoAnnualizeBusiness
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.profile?.q3BizDeductions?.sourceAttributes,
                }),
                q4BizDeductions: bundleOrUndefined({
                  value: autoAnnualizeBusiness ? 0 : taxInputResolved?.profile?.q4BizDeductions?.value,
                  sourceAttributes: autoAnnualizeBusiness
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.profile?.q4BizDeductions?.sourceAttributes,
                }),
                ...action.payload.profile,
              },
              wages: {
                selfWages: bundleOrUndefined({
                  value:
                    getValidNumber(taxPlan?.input?.wages?.selfWages?.value) ??
                    taxInputReport.wages?.selfWages?.value ??
                    0,
                  sourceAttributes: 'ANNUAL_USER_ENTERED', // exposed to user
                }),
                spouseWages: bundleOrUndefined({
                  value:
                    getValidNumber(taxPlan?.input?.wages?.spouseWages?.value) ??
                    taxInputReport.wages?.spouseWages?.value ??
                    0,
                  sourceAttributes: 'ANNUAL_USER_ENTERED', // exposed to user
                }),
                selfWagesOther: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.wages?.selfWagesOther?.value,
                    autoAnnualize ? 'ANNUAL_ESTIMATE' : taxInputResolved?.wages?.selfWagesOther?.sourceAttributes,
                    taxInputResolved?.wages?.selfWagesOther?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.wages?.selfWagesOther?.sourceAttributes,
                }),
                spouseWagesOther: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.wages?.spouseWagesOther?.value,
                    autoAnnualize ? 'ANNUAL_ESTIMATE' : taxInputResolved?.wages?.spouseWagesOther?.sourceAttributes,
                    taxInputResolved?.wages?.spouseWagesOther?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.wages?.spouseWagesOther?.sourceAttributes,
                }),
                ...action.payload.wages,
              },
              divInterest: {
                taxableInterest: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.divInterest?.taxableInterest?.value,
                    taxInputResolved?.divInterest?.taxableInterest?.sourceAttributes || 'ANNUAL_ESTIMATE',
                    taxInputResolved?.divInterest?.taxableInterest?.value,
                  ),
                  sourceAttributes:
                    taxInputResolved?.divInterest?.taxableInterest?.sourceAttributes || 'ANNUAL_ESTIMATE',
                }),
                dividends: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.divInterest?.dividends?.value,
                    taxInputResolved?.divInterest?.dividends?.sourceAttributes || 'ANNUAL_ESTIMATE',
                    taxInputResolved?.divInterest?.dividends?.value,
                  ),
                  sourceAttributes: taxInputResolved?.divInterest?.dividends?.sourceAttributes || 'ANNUAL_ESTIMATE',
                }),
                qualifiedDividends: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.divInterest?.qualifiedDividends?.value,
                    taxInputResolved?.divInterest?.qualifiedDividends?.sourceAttributes || 'ANNUAL_ESTIMATE',
                    taxInputResolved?.divInterest?.qualifiedDividends?.value,
                  ),
                  sourceAttributes:
                    taxInputResolved?.divInterest?.qualifiedDividends?.sourceAttributes || 'ANNUAL_ESTIMATE',
                }),
                ...action.payload.divInterest,
              },
              capitalGains: {
                shortTermGains: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.capitalGains?.shortTermGains?.value,
                    taxInputResolved?.capitalGains?.shortTermGains?.sourceAttributes || 'ANNUAL_ESTIMATE',
                    taxInputResolved?.capitalGains?.shortTermGains?.value,
                  ),
                  sourceAttributes:
                    taxInputResolved?.capitalGains?.shortTermGains?.sourceAttributes || 'ANNUAL_ESTIMATE', // exposed to user
                }),
                longTermGains: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.capitalGains?.longTermGains?.value,
                    taxInputResolved?.capitalGains?.longTermGains?.sourceAttributes || 'ANNUAL_ESTIMATE',
                    taxInputResolved?.capitalGains?.longTermGains?.value,
                  ),
                  sourceAttributes:
                    taxInputResolved?.capitalGains?.longTermGains?.sourceAttributes || 'ANNUAL_ESTIMATE', // exposed to user
                }),
                unrecapturedDepreciationGains: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.capitalGains?.unrecapturedDepreciationGains?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.capitalGains?.unrecapturedDepreciationGains?.sourceAttributes,
                    taxInputResolved?.capitalGains?.unrecapturedDepreciationGains?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.capitalGains?.unrecapturedDepreciationGains?.sourceAttributes,
                }),
                longTerm28PctGains: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.capitalGains?.longTerm28PctGains?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.capitalGains?.longTerm28PctGains?.sourceAttributes,
                    taxInputResolved?.capitalGains?.longTerm28PctGains?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.capitalGains?.longTerm28PctGains?.sourceAttributes,
                }),
                shortTermCarryovers: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.capitalGains?.shortTermCarryovers?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.capitalGains?.shortTermCarryovers?.sourceAttributes,
                    taxInputResolved?.capitalGains?.shortTermCarryovers?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.capitalGains?.shortTermCarryovers?.sourceAttributes,
                }),
                longTermCarryovers: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.capitalGains?.longTermCarryovers?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.capitalGains?.longTermCarryovers?.sourceAttributes,
                    taxInputResolved?.capitalGains?.longTermCarryovers?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.capitalGains?.longTermCarryovers?.sourceAttributes,
                }),
                shortTermGainsAltMin: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.capitalGains?.shortTermGainsAltMin?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.capitalGains?.shortTermGainsAltMin?.sourceAttributes,
                    taxInputResolved?.capitalGains?.shortTermGainsAltMin?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.capitalGains?.shortTermGainsAltMin?.sourceAttributes,
                }),
                unrecapturedDepreciationGainsAltMin: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.capitalGains?.unrecapturedDepreciationGainsAltMin?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.capitalGains?.unrecapturedDepreciationGainsAltMin?.sourceAttributes,
                    taxInputResolved?.capitalGains?.unrecapturedDepreciationGainsAltMin?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.capitalGains?.unrecapturedDepreciationGainsAltMin?.sourceAttributes,
                }),
                longTerm28PctGainsAltMin: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.capitalGains?.longTerm28PctGainsAltMin?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.capitalGains?.longTerm28PctGainsAltMin?.sourceAttributes,
                    taxInputResolved?.capitalGains?.longTerm28PctGainsAltMin?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.capitalGains?.longTerm28PctGainsAltMin?.sourceAttributes,
                }),
                ...action.payload.capitalGains,
              },
              businessIncome: {
                // TODO: ask QCS to pass BizIncome and BizDeductions directly to calc to avoid this mess
                // but for now append BizIncome to revenueSelf and BizDeductions to otherAllowableExpensesSelf
                revenueSelf: bundleOrUndefined({
                  value:
                    (getAnnualValue(
                      year,
                      taxInputReport.businessIncome?.revenueSelf?.value,
                      autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'ANNUAL_USER_ENTERED',
                      getValidNumber(taxInputReport.businessIncome?.revenueSelf?.value),
                    ) ?? 0) + (autoAnnualizeBusiness ? 0 : bizIncomeAdjustment),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'ANNUAL_USER_ENTERED',
                }),
                otherAllowableExpensesSelf: bundleOrUndefined({
                  value:
                    (getAnnualValue(
                      year,
                      taxInputReport.businessIncome?.otherAllowableExpensesSelf?.value,
                      autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'ANNUAL_USER_ENTERED',
                      getValidNumber(taxInputReport?.businessIncome?.otherAllowableExpensesSelf?.value),
                    ) ?? 0) + (autoAnnualizeBusiness ? 0 : bizDeductionsAdjustment),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'ANNUAL_USER_ENTERED',
                }),
                costOfGoodsSelf: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.businessIncome?.costOfGoodsSelf?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                mealsAndEntertainmentSelf: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.businessIncome?.mealsAndEntertainmentSelf?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                businessUseOfHomeSelf: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.businessIncome?.businessUseOfHomeSelf?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                deductibleBusinessMileageSelf: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.businessIncome?.deductibleBusinessMileageSelf?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                unspecifiedBusinessExpensesSelf: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.businessIncome?.unspecifiedBusinessExpensesSelf?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                revenueSpouse: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.businessIncome?.revenueSpouse?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                costOfGoodsSpouse: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.businessIncome?.costOfGoodsSpouse?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                mealsAndEntertainmentSpouse: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.businessIncome?.mealsAndEntertainmentSpouse?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                otherAllowableExpensesSpouse: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.businessIncome?.otherAllowableExpensesSpouse?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                businessUseOfHomeSpouse: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.businessIncome?.businessUseOfHomeSpouse?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                deductibleBusinessMileageSpouse: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.businessIncome?.deductibleBusinessMileageSpouse?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                unspecifiedBusinessExpensesSpouse: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.businessIncome?.unspecifiedBusinessExpensesSpouse?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                ...action.payload.businessIncome,
              },
              otherIncome: {
                otherIncomeGainsOrLosses: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.otherIncome?.otherIncomeGainsOrLosses?.value,
                    taxInputResolved?.otherIncome?.otherIncomeGainsOrLosses?.sourceAttributes || 'ANNUAL_ESTIMATE',
                    taxInputResolved?.otherIncome?.otherIncomeGainsOrLosses?.value,
                  ),
                  sourceAttributes:
                    taxInputResolved?.otherIncome?.otherIncomeGainsOrLosses?.sourceAttributes || 'ANNUAL_ESTIMATE',
                }),
                taxableRefundOfStateLocalIncomeTax: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.otherIncome?.taxableRefundOfStateLocalIncomeTax?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.otherIncome?.taxableRefundOfStateLocalIncomeTax?.sourceAttributes,
                    taxInputResolved?.otherIncome?.taxableRefundOfStateLocalIncomeTax?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.otherIncome?.taxableRefundOfStateLocalIncomeTax?.sourceAttributes ||
                      'ANNUAL_ESTIMATE', // exposed to user
                }),
                alimonyReceived: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.otherIncome?.alimonyReceived?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.otherIncome?.alimonyReceived?.sourceAttributes,
                    taxInputResolved?.otherIncome?.alimonyReceived?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.otherIncome?.alimonyReceived?.sourceAttributes,
                }),
                taxableIRAPensionDistributions: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.otherIncome?.taxableIRAPensionDistributions?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.otherIncome?.taxableIRAPensionDistributions?.sourceAttributes,
                    taxInputResolved?.otherIncome?.taxableIRAPensionDistributions?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.otherIncome?.taxableIRAPensionDistributions?.sourceAttributes,
                }),
                unemploymentCompensation: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.otherIncome?.unemploymentCompensation?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.otherIncome?.unemploymentCompensation?.sourceAttributes,
                    taxInputResolved?.otherIncome?.unemploymentCompensation?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.otherIncome?.unemploymentCompensation?.sourceAttributes,
                }),
                taxableSocialSecurityBenefits: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.otherIncome?.taxableSocialSecurityBenefits?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.otherIncome?.taxableSocialSecurityBenefits?.sourceAttributes,
                    taxInputResolved?.otherIncome?.taxableSocialSecurityBenefits?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.otherIncome?.taxableSocialSecurityBenefits?.sourceAttributes,
                }),
                socialSecurityRRAIncome: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.otherIncome?.socialSecurityRRAIncome?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.otherIncome?.socialSecurityRRAIncome?.sourceAttributes,
                    taxInputResolved?.otherIncome?.socialSecurityRRAIncome?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.otherIncome?.socialSecurityRRAIncome?.sourceAttributes,
                }),
                ...action.payload.otherIncome,
              },
              scheduleE: {
                partnershipIncome: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleE?.partnershipIncome?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                rents: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleE?.rents?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                royalties: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleE?.royalties?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                depreciation: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleE?.depreciation?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                rentalPropertyMileage: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleE?.rentalPropertyMileage?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                expenses: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleE?.expenses?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                unspecifiedRentalPropertyExpense: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleE?.unspecifiedRentalPropertyExpense?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                ...action.payload.scheduleE,
              },
              scheduleF: {
                sales: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleF?.sales?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                supplementalPayments: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleF?.supplementalPayments?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                othIncome: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleF?.othIncome?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                expenses: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleF?.expenses?.value,
                    autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                  ),
                  sourceAttributes: autoAnnualizeBusiness ? 'ANNUAL_ESTIMATE' : 'YEAR_TO_DATE_REPORT',
                }),
                ...action.payload.scheduleF,
              },
              adjustments: {
                allowableIRADeduction: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.adjustments?.allowableIRADeduction?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.adjustments?.allowableIRADeduction?.sourceAttributes,
                    taxInputResolved?.adjustments?.allowableIRADeduction?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.adjustments?.allowableIRADeduction?.sourceAttributes,
                }),
                allowableSEHealthInsuranceDeduction: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.adjustments?.allowableSEHealthInsuranceDeduction?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.adjustments?.allowableSEHealthInsuranceDeduction?.sourceAttributes,
                    taxInputResolved?.adjustments?.allowableSEHealthInsuranceDeduction?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.adjustments?.allowableSEHealthInsuranceDeduction?.sourceAttributes,
                }),
                keoghSEPDeduction: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.adjustments?.keoghSEPDeduction?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.adjustments?.keoghSEPDeduction?.sourceAttributes,
                    taxInputResolved?.adjustments?.keoghSEPDeduction?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.adjustments?.keoghSEPDeduction?.sourceAttributes,
                }),
                penaltyOnEarlySavingsWithdrawal: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.adjustments?.penaltyOnEarlySavingsWithdrawal?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.adjustments?.penaltyOnEarlySavingsWithdrawal?.sourceAttributes,
                    taxInputResolved?.adjustments?.penaltyOnEarlySavingsWithdrawal?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.adjustments?.penaltyOnEarlySavingsWithdrawal?.sourceAttributes,
                }),
                alimonyPaid: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.adjustments?.alimonyPaid?.value,
                    autoAnnualize ? 'ANNUAL_ESTIMATE' : taxInputResolved?.adjustments?.alimonyPaid?.sourceAttributes,
                    taxInputResolved?.adjustments?.alimonyPaid?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.adjustments?.alimonyPaid?.sourceAttributes,
                }),
                movingExpenses: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.adjustments?.movingExpenses?.value,
                    autoAnnualize ? 'ANNUAL_ESTIMATE' : taxInputResolved?.adjustments?.movingExpenses?.sourceAttributes,
                    taxInputResolved?.adjustments?.movingExpenses?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.adjustments?.movingExpenses?.sourceAttributes,
                }),
                otherAdjustments: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.adjustments?.otherAdjustments?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.adjustments?.otherAdjustments?.sourceAttributes,
                    taxInputResolved?.adjustments?.otherAdjustments?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.adjustments?.otherAdjustments?.sourceAttributes,
                }),
                ...action.payload.adjustments,
              },
              scheduleADeductions: {
                medicalAndDental: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleADeductions?.medicalAndDental?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.scheduleADeductions?.medicalAndDental?.sourceAttributes,
                    taxInputResolved?.scheduleADeductions?.medicalAndDental?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.scheduleADeductions?.medicalAndDental?.sourceAttributes,
                }),
                realEstateAndOtherTaxes: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleADeductions?.realEstateAndOtherTaxes?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.scheduleADeductions?.realEstateAndOtherTaxes?.sourceAttributes,
                    taxInputResolved?.scheduleADeductions?.realEstateAndOtherTaxes?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.scheduleADeductions?.realEstateAndOtherTaxes?.sourceAttributes,
                }),
                deductibleInvestmentInterest: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleADeductions?.deductibleInvestmentInterest?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.scheduleADeductions?.deductibleInvestmentInterest?.sourceAttributes,
                    taxInputResolved?.scheduleADeductions?.deductibleInvestmentInterest?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.scheduleADeductions?.deductibleInvestmentInterest?.sourceAttributes,
                }),
                allowableMortgageAndOtherDeductibleInterest: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleADeductions?.allowableMortgageAndOtherDeductibleInterest?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.scheduleADeductions?.allowableMortgageAndOtherDeductibleInterest
                          ?.sourceAttributes,
                    taxInputResolved?.scheduleADeductions?.allowableMortgageAndOtherDeductibleInterest?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.scheduleADeductions?.allowableMortgageAndOtherDeductibleInterest
                        ?.sourceAttributes,
                }),
                charitableContributions: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleADeductions?.charitableContributions?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.scheduleADeductions?.charitableContributions?.sourceAttributes,
                    taxInputResolved?.scheduleADeductions?.charitableContributions?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.scheduleADeductions?.charitableContributions?.sourceAttributes,
                }),
                deductibleCasualtyLosses: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleADeductions?.deductibleCasualtyLosses?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.scheduleADeductions?.deductibleCasualtyLosses?.sourceAttributes,
                    taxInputResolved?.scheduleADeductions?.deductibleCasualtyLosses?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.scheduleADeductions?.deductibleCasualtyLosses?.sourceAttributes,
                }),
                miscDeductions: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleADeductions?.miscDeductions?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.scheduleADeductions?.miscDeductions?.sourceAttributes,
                    taxInputResolved?.scheduleADeductions?.miscDeductions?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.scheduleADeductions?.miscDeductions?.sourceAttributes,
                }),
                miscDeductionsNoLimit: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.scheduleADeductions?.miscDeductionsNoLimit?.value,
                    taxInputResolved?.scheduleADeductions?.miscDeductionsNoLimit?.sourceAttributes || 'ANNUAL_ESTIMATE',
                    taxInputResolved?.scheduleADeductions?.miscDeductionsNoLimit?.value,
                  ),
                  sourceAttributes:
                    taxInputResolved?.scheduleADeductions?.miscDeductionsNoLimit?.sourceAttributes || 'ANNUAL_ESTIMATE', // exposed to user
                }),
                b65OrOlderSelf: taxInputResolved?.scheduleADeductions?.b65OrOlderSelf,
                b65OrOlderSpouse: taxInputResolved?.scheduleADeductions?.b65OrOlderSpouse,
                bBlindSelf: taxInputResolved?.scheduleADeductions?.bBlindSelf,
                bBlindSpouse: taxInputResolved?.scheduleADeductions?.bBlindSpouse,
                ...action.payload.scheduleADeductions,
              },
              stateAndLocalIncomeTax: {
                withholdingsSelf: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.stateAndLocalIncomeTax?.withholdingsSelf?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.stateAndLocalIncomeTax?.withholdingsSelf?.sourceAttributes,
                    taxInputResolved?.stateAndLocalIncomeTax?.withholdingsSelf?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.stateAndLocalIncomeTax?.withholdingsSelf?.sourceAttributes,
                }),
                withholdingsSpouse: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.stateAndLocalIncomeTax?.withholdingsSpouse?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.stateAndLocalIncomeTax?.withholdingsSpouse?.sourceAttributes,
                    taxInputResolved?.stateAndLocalIncomeTax?.withholdingsSpouse?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.stateAndLocalIncomeTax?.withholdingsSpouse?.sourceAttributes,
                }),
                calcProjectedTotalWithholdingSelfAndSpouse: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.stateAndLocalIncomeTax?.calcProjectedTotalWithholdingSelfAndSpouse?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.stateAndLocalIncomeTax?.calcProjectedTotalWithholdingSelfAndSpouse
                          ?.sourceAttributes,
                    taxInputResolved?.stateAndLocalIncomeTax?.calcProjectedTotalWithholdingSelfAndSpouse?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.stateAndLocalIncomeTax?.calcProjectedTotalWithholdingSelfAndSpouse
                        ?.sourceAttributes,
                }),
                ...action.payload.stateAndLocalIncomeTax,
              },
              taxesAndCredits: {
                seOtherIncomeSelf: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.taxesAndCredits?.seOtherIncomeSelf?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.taxesAndCredits?.seOtherIncomeSelf?.sourceAttributes,
                    taxInputResolved?.taxesAndCredits?.seOtherIncomeSelf?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.taxesAndCredits?.seOtherIncomeSelf?.sourceAttributes,
                }),
                seOtherIncomeSpouse: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.taxesAndCredits?.seOtherIncomeSpouse?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.taxesAndCredits?.seOtherIncomeSpouse?.sourceAttributes,
                    taxInputResolved?.taxesAndCredits?.seOtherIncomeSpouse?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.taxesAndCredits?.seOtherIncomeSpouse?.sourceAttributes,
                }),
                seOtherWagesSelf: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.taxesAndCredits?.seOtherWagesSelf?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.taxesAndCredits?.seOtherWagesSelf?.sourceAttributes,
                    taxInputResolved?.taxesAndCredits?.seOtherWagesSelf?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.taxesAndCredits?.seOtherWagesSelf?.sourceAttributes,
                }),
                seOtherWagesSpouse: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.taxesAndCredits?.seOtherWagesSpouse?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.taxesAndCredits?.seOtherWagesSpouse?.sourceAttributes,
                    taxInputResolved?.taxesAndCredits?.seOtherWagesSpouse?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.taxesAndCredits?.seOtherWagesSpouse?.sourceAttributes,
                }),
                amtOtherAdjustmentsAndTaxPreferenceItems: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.taxesAndCredits?.amtOtherAdjustmentsAndTaxPreferenceItems?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.taxesAndCredits?.amtOtherAdjustmentsAndTaxPreferenceItems?.sourceAttributes,
                    taxInputResolved?.taxesAndCredits?.amtOtherAdjustmentsAndTaxPreferenceItems?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.taxesAndCredits?.amtOtherAdjustmentsAndTaxPreferenceItems?.sourceAttributes,
                }),
                otherTax: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.taxesAndCredits?.otherTax?.value,
                    autoAnnualize ? 'ANNUAL_ESTIMATE' : taxInputResolved?.taxesAndCredits?.otherTax?.sourceAttributes,
                    taxInputResolved?.taxesAndCredits?.otherTax?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.taxesAndCredits?.otherTax?.sourceAttributes,
                }),
                credits: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.taxesAndCredits?.credits?.value,
                    autoAnnualize ? 'ANNUAL_ESTIMATE' : taxInputResolved?.taxesAndCredits?.credits?.sourceAttributes,
                    taxInputResolved?.taxesAndCredits?.credits?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.taxesAndCredits?.credits?.sourceAttributes,
                }),
                amtMedicalExpAdjustmentsItems: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.taxesAndCredits?.amtMedicalExpAdjustmentsItems?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.taxesAndCredits?.amtMedicalExpAdjustmentsItems?.sourceAttributes,
                    taxInputResolved?.taxesAndCredits?.amtMedicalExpAdjustmentsItems?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.taxesAndCredits?.amtMedicalExpAdjustmentsItems?.sourceAttributes,
                }),
                niiagiAdj: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.taxesAndCredits?.niiagiAdj?.value,
                    autoAnnualize ? 'ANNUAL_ESTIMATE' : taxInputResolved?.taxesAndCredits?.niiagiAdj?.sourceAttributes,
                    taxInputResolved?.taxesAndCredits?.niiagiAdj?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.taxesAndCredits?.niiagiAdj?.sourceAttributes,
                }),
                ...action.payload.taxesAndCredits,
              },
              withholdingsAndSummary: {
                withholdingsSelf: bundleOrUndefined({
                  value: getValidNumber(taxPlan?.input?.withholdingsAndSummary?.withholdingsSelf?.value) ?? 0,
                  sourceAttributes: 'ANNUAL_USER_ENTERED', // exposed to user, so ignore report
                }),
                withholdingsSpouse: bundleOrUndefined({
                  value: getValidNumber(taxPlan?.input?.withholdingsAndSummary?.withholdingsSpouse?.value) ?? 0,
                  sourceAttributes: 'ANNUAL_USER_ENTERED', // exposed to user, so ignore report
                }),
                otherWithholdingsSelf: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.withholdingsAndSummary?.otherWithholdingsSelf?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.withholdingsAndSummary?.otherWithholdingsSelf?.sourceAttributes,
                    taxInputResolved?.withholdingsAndSummary?.otherWithholdingsSelf?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.withholdingsAndSummary?.otherWithholdingsSelf?.sourceAttributes,
                }),
                otherWithholdingsSpouse: bundleOrUndefined({
                  value: getAnnualValue(
                    year,
                    taxInputReport.withholdingsAndSummary?.otherWithholdingsSpouse?.value,
                    autoAnnualize
                      ? 'ANNUAL_ESTIMATE'
                      : taxInputResolved?.withholdingsAndSummary?.otherWithholdingsSpouse?.sourceAttributes,
                    taxInputResolved?.withholdingsAndSummary?.otherWithholdingsSpouse?.value,
                  ),
                  sourceAttributes: autoAnnualize
                    ? 'ANNUAL_ESTIMATE'
                    : taxInputResolved?.withholdingsAndSummary?.otherWithholdingsSpouse?.sourceAttributes,
                }),
                ...action.payload.withholdingsAndSummary,
              },
            });
            taxInput = taxesTypes.mkTaxInput(taxInputReport);
          } catch (error: any) {
            assert(false, error);
            action.meta?.reject?.({ state: 'TAX_INPUT_ERROR' });
            yield put(actions.performTaxPlanRefreshFailure(new Error('Tax input transformation failed')));
            return { state: 'TAX_INPUT_ERROR' };
          }
          if (!taxInput) {
            action.meta?.reject?.({ state: 'TAX_INPUT_ERROR' });
            yield put(actions.performTaxPlanRefreshFailure(new Error('Tax input is empty')));
            return { state: 'TAX_INPUT_ERROR' };
          }

          // update/create tax input
          let taxPlanId;
          if (action.payload.id) {
            yield put(actions.updateTaxInput(taxInput, { axiosConfig: { params: { doReplace: true } } }));
            const { updateTaxInputSuccess } = yield race({
              updateTaxInputSuccess: take(actions.updateTaxInputSuccess),
              updateTaxInputFailure: take(actions.updateTaxInputFailure),
            });
            taxPlanId = updateTaxInputSuccess?.payload?.id;
          } else {
            yield put(actions.createTaxInput(taxInput));
            const { createTaxInputSuccess } = yield race({
              createTaxInputSuccess: take(actions.createTaxInputSuccess),
              createTaxInputFailure: take(actions.createTaxInputFailure),
            });
            taxPlanId = createTaxInputSuccess?.payload?.id;
          }
          if (!taxPlanId) {
            action.meta?.reject?.({ state: 'TAX_INPUT_ERROR' });
            yield put(actions.performTaxPlanRefreshFailure(new Error('Tax input creation failed')));
            return { state: 'TAX_INPUT_ERROR' };
          }

          // calculate taxes
          const { performTaxCalcSuccess, performTaxCalcFailure } = yield call(performTaxCalcForPlan, taxPlanId);
          const taxCalculation = performTaxCalcSuccess?.payload?.data?.resource?.output?.withholdingsAndSummaryCalc;
          if (!taxCalculation) {
            assert(false, 'tax calc error', performTaxCalcFailure?.payload ?? performTaxCalcSuccess?.payload);
            action.meta?.reject?.({ state: 'CALCULATION_ERROR' });
            yield put(actions.performTaxPlanRefreshFailure(new Error('Tax calculation failed')));
            return { state: 'CALCULATION_ERROR' };
          }

          // create/update recurring based on WithholdingsAndSummary_CALC_RemainingTaxDue
          // TODO: make it smarter and update instances of the model
          const taxPlanStats = yield select(taxesSelectors.getTaxPlanStats, taxPlanId);
          const stModelId = taxPlan?.input?.profile?.stModelId;
          if (action.payload.id && stModelId && !action.meta?.props?.recurrenceAccountId) {
            // update
            const upcomingTransactionsCount = taxPlanStats.transactionsUpcoming.length;
            if (upcomingTransactionsCount) {
              const taxQuarterAmount = Math.min(0, taxCalculation.remainingTaxDue / upcomingTransactionsCount);
              const existingScheduledTransaction = yield select(
                scheduledTransactionsSelectors.getScheduledTransactionById,
                stModelId,
              );
              if (
                existingScheduledTransaction &&
                existingScheduledTransaction.transaction.amount !== taxQuarterAmount
              ) {
                yield put(
                  scheduledTransactionsActions.updateScheduledTransaction(
                    scheduledTransactionsTypes.mkScheduledTransactionPatch({
                      id: stModelId,
                      transaction: transactionsTypes.mkCashFlowTransactionPatch({
                        amount: taxQuarterAmount,
                      }),
                    }),
                  ),
                );
                const { updateScheduledTransactionSuccess } = yield race({
                  updateScheduledTransactionSuccess: take(
                    scheduledTransactionsActions.updateScheduledTransactionSuccess,
                  ),
                  updateScheduledTransactionFailure: take(
                    scheduledTransactionsActions.updateScheduledTransactionFailure,
                  ),
                });
                if (!updateScheduledTransactionSuccess) {
                  action.meta?.reject?.({ state: 'SCHEDULED_TRANSACTION_UPDATE_ERROR' });
                  yield put(actions.performTaxPlanRefreshFailure(new Error('Tax input update failed')));
                  return { state: 'SCHEDULED_TRANSACTION_UPDATE_ERROR' };
                }
              }
            }
          } else if (action.meta?.props?.recurrenceAccountId) {
            // create
            const upcomingTransactionsCount = 4;
            const taxQuarterAmount = Math.min(0, taxCalculation.remainingTaxDue / upcomingTransactionsCount);

            yield put(
              scheduledTransactionsActions.createScheduledTransaction(
                scheduledTransactionsUtils.mkEstimatedTaxesModel(year, {
                  transaction: {
                    accountId: action.meta?.props.recurrenceAccountId,
                    amount: taxQuarterAmount,
                    postedOn: '',
                  },
                }),
              ),
            );
            const { createScheduledTransactionSuccess } = yield race({
              createScheduledTransactionSuccess: take(scheduledTransactionsActions.createScheduledTransactionSuccess),
              createScheduledTransactionFailure: take(scheduledTransactionsActions.createScheduledTransactionFailure),
            });
            if (!createScheduledTransactionSuccess) {
              action.meta?.reject?.({ state: 'SCHEDULED_TRANSACTION_CREATE_ERROR' });
              yield put(actions.performTaxPlanRefreshFailure(new Error('Tax input creation failed')));
              return { state: 'SCHEDULED_TRANSACTION_CREATE_ERROR' };
            }

            // update tax input with stModelId
            yield put(
              actions.updateTaxInput(
                taxesTypes.mkTaxInput({
                  id: taxPlanId,
                  profile: { stModelId: createScheduledTransactionSuccess?.payload?.id },
                }),
              ),
            );
            const { updateTaxInputSuccess } = yield race({
              updateTaxInputSuccess: take(actions.updateTaxInputSuccess),
              updateTaxInputFailure: take(actions.updateTaxInputFailure),
            });
            if (!updateTaxInputSuccess) {
              action.meta?.reject?.({ state: 'TAX_INPUT_UPDATE_ERROR' });
              yield put(actions.performTaxPlanRefreshFailure(new Error('Tax input update failed')));
              return { state: 'TAX_INPUT_UPDATE_ERROR' };
            }
          }

          // complete
          action.meta?.resolve?.({ state: 'COMPLETE' });
          yield put(actions.performTaxPlanRefreshSuccess());
        }
      } catch (error: any) {
        assert(false, error);
        action.meta?.reject?.({ state: 'TAX_PLAN_REFRESH_ERROR' });
        yield put(actions.performTaxPlanRefreshFailure(new Error('Tax plan refresh failed')));
        return { state: 'TAX_PLAN_REFRESH_ERROR' };
      }
    },
  );
}

export default [
  getTaxCalculationsActionWatcher,
  createTaxInputActionWatcher,
  updateTaxInputActionWatcher,
  deleteTaxInputActionWatcher,
  getTaxInputsActionWatcher,
  createTaxInputFailureActionWatcher,
  performTaxCalcActionWatcher,
  performTaxPlanRefreshActionWatcher,
];
